<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useEventListener,
  useMediaQuery,
  useSessionStorage,
} from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { registerSubscription } from 'dashboard/helper/pushHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Logo from 'dashboard/components-next/icon/Logo.vue';

const props = defineProps({
  registrationFailed: { type: Boolean, default: false },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const isDesktop = useMediaQuery('(min-width: 768px)');
const dismissed = useSessionStorage(
  `browser-notification-prompt:${currentUser.value.id}`,
  false
);
const isSupported =
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  Boolean(window.chatwootConfig.vapidPublicKey);
const permission = ref(isSupported ? Notification.permission : 'unsupported');
const isEnabling = ref(false);
const hasError = ref(false);
watch(
  () => props.registrationFailed,
  value => {
    hasError.value = value;
  },
  { immediate: true }
);
const isBlocked = computed(() => permission.value === 'denied');
const isVisible = computed(
  () =>
    isSupported &&
    isDesktop.value &&
    !route.meta.mobile &&
    !dismissed.value &&
    (permission.value !== 'granted' || hasError.value || isEnabling.value)
);

const dismiss = () => {
  dismissed.value = true;
};

useEventListener(window, 'focus', () => {
  if (isSupported && !isEnabling.value) {
    permission.value = Notification.permission;
  }
});

const enableNotifications = async () => {
  if (isBlocked.value) {
    router.push({ name: 'profile_settings_notifications' });
    dismiss();
    return;
  }

  isEnabling.value = true;
  hasError.value = false;
  try {
    // Request directly from the click to preserve the browser's user gesture.
    permission.value = await Notification.requestPermission();
    if (permission.value !== 'granted') return;

    await navigator.serviceWorker.register('/sw.js');
    await registerSubscription(dismiss, () => {
      hasError.value = true;
    });
  } catch {
    hasError.value = true;
  } finally {
    isEnabling.value = false;
  }
};
</script>

<template>
  <section
    v-if="isVisible"
    role="dialog"
    aria-modal="false"
    aria-labelledby="browser-notification-title"
    aria-describedby="browser-notification-description"
    class="fixed left-1/2 top-4 z-50 w-[500px] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl bg-n-solid-2 p-6 text-n-slate-12 shadow-xl"
    @keydown.esc.stop="dismiss"
  >
    <div class="flex items-start gap-4">
      <Logo class="!size-16 shrink-0 object-contain" aria-hidden="true" />
      <div class="min-w-0 flex-1">
        <h2
          id="browser-notification-title"
          class="m-0 text-base font-medium leading-6"
        >
          {{ t('BROWSER_NOTIFICATION_PROMPT.TITLE') }}
        </h2>
        <p
          id="browser-notification-description"
          class="m-0 text-sm leading-6 text-n-slate-11"
        >
          {{
            t(
              `BROWSER_NOTIFICATION_PROMPT.${isBlocked ? 'BLOCKED' : 'DESCRIPTION'}`
            )
          }}
        </p>
      </div>
    </div>
    <p v-if="hasError" role="alert" class="mb-0 mt-4 text-sm text-n-ruby-11">
      {{ t('BROWSER_NOTIFICATION_PROMPT.ERROR') }}
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <Button
        variant="ghost"
        size="lg"
        :label="t('BROWSER_NOTIFICATION_PROMPT.LATER')"
        @click="dismiss"
      />
      <Button
        size="lg"
        :label="
          t(`BROWSER_NOTIFICATION_PROMPT.${isBlocked ? 'SETTINGS' : 'ENABLE'}`)
        "
        :is-loading="isEnabling"
        :disabled="isEnabling"
        @click="enableNotifications"
      />
    </div>
  </section>
</template>
